import { inject, injectable } from "inversify";
import IOperationsHistoryService from "./IOperationsHistoryService";
import { replaceCurrencySymb } from "../../utilities/parseHelper";
import { StatementItemModel } from "../../models/statement/statementItemModel";
import { RequestParamsModel } from "../../models/statement/requestParamsModel";
import { OperationDetailsModel } from "../../models/operations/operationDetailsModel";
import { DeclarationPropertyValueData } from "../../data/declarationPropertyValueData";
import { DeclarationPropertiesTypes } from "../../data/enums/declarationPropertiesTypes";
import { OperationRequestData } from "../../data/operationRequestData";
import { OperationsHistoryApi } from "../api/OperationsHistoryApi";
import IAlertService from "../alert/IAlertService";
import IErrorService from "../error/IErrorService";
import sumFilter from "../../filters/sum.filter";
import { DiTokens } from "../diTokens";
import i18n from "@/i18n";
import { dateFilter } from "../../filters/date.filter";
import { AccountStatementOperationData } from "../../data/accountStatementOperationData";

@injectable()
/** Сервис выписки по счету */
export default class ContractStatementService
  implements IOperationsHistoryService {
  constructor(
    @inject(DiTokens.AlertService) private alertService: IAlertService,
    @inject(DiTokens.ErrorService) private errorService: IErrorService
  ) { }

  public async getOperationsHistory(params: RequestParamsModel): Promise<any> {
    try {
      /** При отсутствии дат отправляем на сервер значения с 1 числа месяца по сегодняшний день */
      if (!params.dateBegin) {
        const nowDate = new Date();
        params.dateBegin = `${nowDate.getFullYear()}-${nowDate.getMonth() + 1 < 10
            ? "0" + (nowDate.getMonth() + 1)
            : nowDate.getMonth() + 1
          }-01`;
      }
      if (!params.dateEnd) {
        const nowDate = new Date();
        params.dateEnd = `${nowDate.getFullYear()}-${nowDate.getMonth() + 1 < 10
            ? "0" + (nowDate.getMonth() + 1)
            : nowDate.getMonth() + 1
          }-${nowDate.getDate() < 10 ? "0" + nowDate.getDate() : nowDate.getDate()
          }`;
      }
      const resData = await OperationsHistoryApi.getAccountStatement(params);
      const resultArray = resData.Operations.map(
        item =>
          new StatementItemModel(
            !item.CommitDate || item.CommitDate?.toString() == 'Invalid Date' ? item.RegDate : item.CommitDate,
            item.MCCImageUrl,
            item.Description,
            2,
            item.Sum,
            resData.Currency,
            item.MCCName,
            null,
            item.Reference,
            '',
            false,
            item.MCCColor,
            item.Status,
            this.parseDetails(item),
            item.Details,
            item.IsSbp
          )
      );
      const result = {
        list: resultArray,
        RestIn: sumFilter(Math.abs(resData.BeginRest), false, resData.Currency),
        RestOut: sumFilter(Math.abs(resData.EndRest), false, resData.Currency)
      };
      if (result) return result;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return [];
  }

  public async getOperationDetails(
    operation: any
  ): Promise<OperationDetailsModel> {
    try {
      const requestData = await OperationsHistoryApi.getOperationAsync(
        0,
        operation.ReferenceId
      );
      const respondentCode = parseInt(requestData.respondentCode);
      if (!operation.IsRespondentActive) 
        operation.IsRespondentActive = requestData.isRespondentActive;
      if (requestData.operationId)
        operation.Id = requestData.operationId;
      const operationDetail = new OperationDetailsModel(operation, requestData, null, operation.modalDetails);

      if (!isNaN(respondentCode)) {
        operationDetail.RespondentData = await OperationsHistoryApi.getRespondentAsync(
          respondentCode
        );
      }
      // operationDetail.RequestData?.details.forEach(
      //   (d: DeclarationPropertyValueData) => {
      //     d.Value = replaceCurrencySymb(d.Value);
      //   }
      // );

      // if (operationDetail.RequestData?.details)
      //   operationDetail.Details = operationDetail.RequestData?.details;
       
      return operationDetail;
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
      throw e;
    }
    return operation;
  }

  public async getStatementFile(
    id: number,
    dateBegin: string,
    dateEnd: string
  ): Promise<string> {
    const ord: OperationRequestData = new OperationRequestData(null);
    let link: string = "";
    ord.respondentCode = "ContractStatementToPdf";
    ord.setProperty(
      "contractId",
      id.toString(),
      DeclarationPropertiesTypes.Int
    );
    ord.setProperty(
      "dateBegin",
      dateBegin,
      DeclarationPropertiesTypes.DateTime
    );
    ord.setProperty("dateEnd", `${dateEnd} 23:59:59`, DeclarationPropertiesTypes.DateTime);
    try {
      link = await OperationsHistoryApi.getHistoryPdf(ord);
    } catch (e) {
      await this.errorService.errorHandler(e, false, true, "");
    }
    return link;
  }

  /**
   * Выписываем детали операции из данных операции
   * @param data 
   * @returns Детали операции
   */
  private parseDetails(data: AccountStatementOperationData): DeclarationPropertyValueData[] {
    const details: DeclarationPropertyValueData[] = [
      {
        //Дата совершения платежа
        Key: i18n.t("main.operations.contractStatementData.CommitDate"),
        Value: dateFilter(
          data.CommitDate,
          "datetime"
        ),
        Image: ""
      },
      {
        //Дата зачисления/списания
        Key: i18n.t("main.operations.contractStatementData.RegDate"),
        Value: dateFilter(
          data.RegDate,
          "date"
        ),
        Image: ""
      },
      {
        //Сумма
        Key: i18n.t("main.operations.contractStatementData.Sum"),
        Value: sumFilter(
          data.Sum,
          false
        ),
        Image: ""
      },
      {
        //Описание транзакции
        Key: i18n.t("main.operations.contractStatementData.Description"),
        Value: data.Description,
        Image: ""
      },
      {
        //Референс счёта"
        Key: i18n.t("main.operations.contractStatementData.Reference"),
        Value: data.Reference,
        Image: ""
      },
      {
        //Наименование категории
        Key: i18n.t("main.operations.contractStatementData.MCCName"),
        Value: data.MCCName,
        Image: ""
      },
    ];
    return details;
  }
}
